import type { CalendarData, CalendarEvent } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { Resource } from '../components/Resource';
import { CardHead, DemoBadge, LockedNote, Notice, PageHead } from '../components/ui';
import { fmt, sameDay } from '../lib/format';
import { usePrefs } from '../lib/prefs';
import { RoutineLive } from './RoutineLive';

const BAR: Record<CalendarEvent['color'], string> = { accent: '', signal: 'c-signal', info: 'c-info', demo: 'c-demo' };

function groupByDay(events: CalendarEvent[]) {
  const days: { date: string; events: CalendarEvent[] }[] = [];
  [...events].sort((a, b) => a.start.localeCompare(b.start)).forEach((e) => {
    let d = days.find((x) => sameDay(x.date, e.start));
    if (!d) { d = { date: e.start, events: [] }; days.push(d); }
    d.events.push(e);
  });
  return days;
}

function EventRow({ e }: { e: CalendarEvent }) {
  return (
    <div className="event">
      <div className="when">{fmt.time(e.start)}<small>{fmt.time(e.end)}</small></div>
      <span className={`bar ${BAR[e.color]}`} />
      <div>
        <div className="title">{e.title}</div>
        <div className="sub">{e.where} · {e.calendar}</div>
        {(e.tentative || e.conflict) && (
          <div className="chips mt-10">
            {e.tentative && <span className="badge badge-accent">Provisório · preparado, não enviado</span>}
            {e.conflict && <span className="badge badge-warn"><Icon name="alert" />{e.conflict}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function Agenda({ d }: { d: CalendarData }) {
  const now = new Date();
  return (
    <div className="agenda-layout">
      <div className="agenda-days">
        {groupByDay(d.events).map((day) => (
          <section key={day.date} className="card">
            <div className="day-head"><h3>{fmt.weekday(day.date)}, {fmt.short(day.date)}</h3>{sameDay(day.date, now) && <span className="today">Hoje</span>}</div>
            {day.events.map((e) => <EventRow key={e.id} e={e} />)}
          </section>
        ))}
      </div>
      <div className="stack">
        <section className="card">
          <CardHead icon="spark" title="Observações" extra={<DemoBadge />} />
          <div className="stack">{d.suggestions.map((s) => <Notice key={s.id} kind={s.kind === 'warn' ? 'warn' : undefined} icon={s.kind === 'warn' ? 'alert' : 'info'}>{s.text}</Notice>)}</div>
        </section>
        <section className="card">
          <CardHead icon="sun" title="Rotina" extra={<DemoBadge />} />
          <div className="routine">{d.routine.map((r) => <div key={r.time + r.label} className="routine-item"><span className="time-pill">{r.time}</span><span>{r.label}</span></div>)}</div>
        </section>
        <LockedNote>Criar, alterar ou recusar eventos e enviar convites não existe nesta etapa. Quando existir, nada será enviado sem sua confirmação.</LockedNote>
      </div>
    </div>
  );
}

export function Calendar() {
  const { stateMode } = usePrefs();
  const calendarEmpty = { icon: 'calendar' as const, title: 'Nenhum calendário conectado', text: 'A leitura do Google Agenda virá na Etapa 5, com permissão somente de leitura e autorizada por você.', actions: [{ label: 'Ver integrações planejadas', href: '#/configuracoes/integracoes', icon: 'plug' as const }] };
  return (
    <div className="page">
      <PageHead title="Agenda e rotina" lead="Próximos sete dias, conflitos detectados e a rotina que você definir." />
      {stateMode === 'demo' ? (
        <Resource name="calendar" skeleton="list" skeletonCount={6} empty={calendarEmpty}>
          {(d) => <Agenda d={d} />}
        </Resource>
      ) : (
        <div className="agenda-layout">
          <Resource name="calendar" skeleton="list" skeletonCount={4} empty={calendarEmpty}>
            {(d) => <Agenda d={d} />}
          </Resource>
          <div className="stack">
            <RoutineLive />
            <LockedNote>Criar, alterar ou recusar eventos e enviar convites não existe nesta etapa. Quando existir, nada será enviado sem sua confirmação.</LockedNote>
          </div>
        </div>
      )}
    </div>
  );
}
